import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from './modal';
import { Button } from './button';
import { userAPI } from '../../services/api';

const ProfileModal = ({ isOpen, onClose, profileData, userRole, loading }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({});

  // Initialize form data when profileData changes
  useEffect(() => {
    if (profileData) {
      setFormData({ ...profileData });
    }
  }, [profileData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset form data to original profileData
    setFormData({ ...profileData });
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      console.log('Updating user profile with data:', formData);
      await userAPI.updateUserProfile(formData);
      alert('Profile updated successfully!');
      setIsEditing(false);
      // Optionally trigger a refresh of the profile data
      if (onClose) {
        onClose(); // Close modal and let parent refresh data
      }
    } catch (error) {
      console.error('Error updating user profile:', error);
      alert(`Failed to update profile: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!profileData && !loading) {
    return null;
  }

  const renderField = (label, field, type = 'text') => {
    const value = isEditing ? formData[field] : profileData[field];
    
    if (isEditing) {
      return (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
          <input
            type={type}
            value={value || ''}
            onChange={(e) => handleInputChange(field, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      );
    }
    
    return (
      <div>
        <label className="text-sm font-medium text-gray-500">{label}</label>
        <p className="text-gray-900">{value || 'N/A'}</p>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-4xl max-h-[90vh] flex flex-col">
      <ModalHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">User Profile</h2>
          {!loading && profileData && !isEditing && (
            <Button
              onClick={handleEdit}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              size="sm"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Profile
            </Button>
          )}
        </div>
      </ModalHeader>
      
      <ModalBody className="space-y-4 flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="ml-3 text-gray-600">Loading profile...</p>
          </div>
        ) : profileData ? (
          <div className="space-y-6">
            {/* Profile Image Section */}
            {profileData.image && (
              <div className="flex justify-center">
                <div className="text-center">
                  <label className="block text-sm font-medium text-gray-500 mb-2">Profile Image</label>
                  <div className="relative">
                    <img
                      src={profileData.image}
                      alt={`${profileData.first_name} ${profileData.last_name}` || 'Profile'}
                      className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200 shadow-sm"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
            
            {/* Profile Data Grid */}
            <div className="space-y-6">
              {/* Always visible fields for all roles - 2 columns */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderField('Email', 'email', 'email')}
                {renderField('Phone Number', 'phone_number', 'tel')}
                {renderField('First Name', 'first_name')}
                {renderField('Last Name', 'last_name')}
              </div>

              {/* Conditional fields - only show for non-instructor roles */}
              {userRole !== 'instructor' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderField('Student Number', 'student_number')}
                  {renderField('Register Number', 'rd')}
                  {renderField('Course', 'course')}
                  {renderField('Level', 'level')}
                  {renderField('Mode', 'mode')}
                  {renderField('Class', 'class')}
                  {renderField('Payment', 'payment', 'number')}
                  {renderField('Payment Remaining', 'payment_remaining', 'number')}
                  {renderField('Student ID', 'student_id')}
                  {renderField('Initials', 'initials')}
                  {renderField('Net ID', 'net_id')}
                  {renderField('Combined Name', 'combined_name')}
                  {renderField('Class ID', 'class_id')}
                  {renderField('Exam Date', 'exam_date', 'date')}
                  {renderField('Exam Result', 'exam_result')}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-600">No profile data available</p>
          </div>
        )}
      </ModalBody>
      
      <ModalFooter className="flex-shrink-0">
        {isEditing ? (
          <div className="flex space-x-2">
            <Button 
              onClick={handleCancel} 
              variant="outline"
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2 inline-block"></div>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        ) : (
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default ProfileModal;